import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock } from "lucide-react";
import api, { tierPerks } from "@/lib/api";

const CATEGORIES = ["Classic", "Trending", "Luxury", "Festival", "Exclusive"];
const COMBOS = [1, 10, 99];

export default function GiftTray({ open, onClose, coins, userTier, onSend }) {
  const [gifts, setGifts] = useState([]);
  const [cat, setCat] = useState("Classic");
  const [selected, setSelected] = useState(null);
  const [combo, setCombo] = useState(1);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/gifts").then(({ data }) => setGifts(data.gifts)).catch(() => {});
    }
  }, [open]);

  const filtered = gifts.filter((g) => g.category === cat);
  const locked = cat === "Exclusive" && !tierPerks(userTier).exclusive_gifts;
  const totalCost = selected ? selected.price * combo : 0;

  const handleSend = async () => {
    if (!selected || sending || locked) return;
    setSending(true);
    await onSend(selected, combo);
    setSending(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 z-[10000]" onClick={onClose}
          />
          <motion.div
            data-testid="gift-tray"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className="absolute bottom-0 left-0 right-0 z-[10001] sheet-luxe max-h-[64%] flex flex-col pb-3"
          >
            <div className="flex justify-center pt-2.5 pb-1">
              <span className="w-10 h-1 rounded-full bg-white/20" />
            </div>
            <div className="flex items-center px-5 pt-1">
              <div className="flex-1 flex items-center justify-between overflow-x-auto no-scrollbar gap-3">
                {CATEGORIES.map((c) => (
                  <button
                    key={c} data-testid={`gift-cat-${c.toLowerCase()}`}
                    onClick={() => { setCat(c); setSelected(null); }}
                    className={`relative shrink-0 text-[13px] font-bold pb-2 transition-colors ${cat === c ? "text-white" : "text-[#6E6683]"}`}
                  >
                    {c}
                    {cat === c && <span className="absolute bottom-0 left-0 right-0 h-[3px] rounded-full gradient-primary" />}
                  </button>
                ))}
              </div>
              <button data-testid="gift-tray-close" onClick={onClose} className="text-[#6E6683] ml-3 -mt-1"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-4 gap-2 p-3 overflow-y-auto flex-1 relative">
              {locked && (
                <div className="col-span-4 flex flex-col items-center gap-2 py-8" data-testid="exclusive-locked">
                  <Lock size={26} className="text-[#6E6683]" />
                  <p className="text-[#B8B0C9] text-xs font-semibold text-center">Exclusive gifts unlock at Gold 🥇 on the Navaratna Ladder<br /><span className="text-[#6E6683]">or with 👑 VIP membership</span></p>
                </div>
              )}
              {!locked && filtered.map((g) => (
                <button
                  key={g.id} data-testid={`gift-item-${g.id}`}
                  onClick={() => setSelected(g)}
                  className={`rounded-2xl p-2 pt-2.5 flex flex-col items-center gap-1 transition-all ${selected?.id === g.id ? "gradient-border scale-105" : "bg-[#1E1830] border border-white/[0.04]"}`}
                >
                  <span className="relative">
                    {g.icon_url ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${g.icon_url}`} alt={g.name} className="w-11 h-11 object-contain drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]" />
                    ) : (
                      <span className="text-3xl leading-9">{g.emoji}</span>
                    )}
                    {g.video_url && (
                      <span className="absolute -top-1 -right-2 text-[7px] font-extrabold px-1 py-px rounded bg-violet-500 text-white shadow-[0_0_8px_rgba(139,92,246,0.9)]">FX</span>
                    )}
                  </span>
                  <span className="text-[10px] text-white/85 font-semibold truncate w-full text-center">{g.name}</span>
                  <span className="text-[10px] text-[#FFC857] font-bold flex items-center gap-0.5"><img src="/assets/coin-single.png" alt="" className="w-3 h-3 object-contain" />{g.price.toLocaleString()}</span>
                </button>
              ))}
              {!locked && filtered.length === 0 && <p className="col-span-4 text-center text-[#6E6683] text-xs py-6">No gifts in this category</p>}
            </div>
            <div className="px-4 pt-3 border-t border-[rgba(255,216,107,0.15)]">
              <div className="flex items-center justify-between mb-3">
                <div className="coin-pill px-3.5 py-2 flex items-center gap-1.5 text-sm" data-testid="gift-tray-balance">
                  <img src="/assets/coin-single.png" alt="" className="w-5 h-5 object-contain" /> {coins?.toLocaleString()}
                </div>
                <div className="flex bg-[#1E1830] rounded-full p-1 border border-white/[0.06]">
                  {COMBOS.map((c) => (
                    <button
                      key={c} data-testid={`combo-x${c}`}
                      onClick={() => setCombo(c)}
                      className={`text-xs font-bold px-3.5 py-1.5 rounded-full transition-colors ${combo === c ? "bg-[#FFD86B] text-[#0B0B14]" : "text-[#6E6683]"}`}
                    >
                      x{c}
                    </button>
                  ))}
                </div>
              </div>
              <button
                data-testid="gift-send-button"
                onClick={handleSend}
                disabled={!selected || sending || totalCost > coins}
                className="btn-gradient w-full py-3 text-sm disabled:opacity-40 active:scale-[0.98] transition-transform"
              >
                {selected && totalCost > coins ? "Low balance" : `Send ↑${selected ? ` · ${totalCost.toLocaleString()}` : ""}`}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
